/**
 * Cloud System Settings — Domain API Module
 *
 * Wraps the shared V2 API client for all data operations on the
 * CloudSystemSettingsPage (SQL-backed cloud campuses).
 *
 * All time values are stored in **seconds** in the database and
 * converted to **minutes** in the UI layer (tabs / orchestrator).
 */

import { apiFetch, apiSave, apiTransaction } from '../../../../services/api.js';

// ---------------------------------------------------------------------------
// READ operations
// ---------------------------------------------------------------------------

/** Fetch alert settings for a campus (core_campus_alert_settings). */
export function fetchAlertSettings(campusId) {
    return apiFetch({
        table: 'core_campus_alert_settings',
        procedure: 'fetchbyparameter',
        reqType: 'direct',
        pKey: 'campus_id',
        pVal: campusId,
    });
}

/** Fetch campus row (customization + server fields from core_campus). */
export function fetchCampusRow(campusId) {
    return apiFetch({
        table: 'core_campus',
        procedure: 'fetchbyparameter',
        reqType: 'direct',
        pKey: 'campus_id',
        pVal: campusId,
    });
}

/** Fetch high-priority entity assignments for a campus. */
export function fetchHighPriorityEntities(campusId) {
    return apiFetch({
        table: 'core_high_priority_entity',
        procedure: 'fetchbyparameter',
        reqType: 'direct',
        pKey: 'campus_id',
        pVal: campusId,
    });
}

/** Fetch all event types (for the high-priority checkbox grid). */
export function fetchEventTypes(campusId) {
    return apiFetch({
        table: 'att_event_types',
        procedure: 'fetchall',
        reqType: 'direct',
        pKey: 'campus_id',
        pVal: campusId,
    });
}

/** Fetch currently assigned AFR devices for a campus (stored procedure). */
export function fetchAFRDevices(campusId) {
    return apiFetch({
        procedure: 'getAFRDevices',
        reqType: 'stored',
        pKey: 'campus_id',
        pVal: campusId,
        payload: { campus_id: campusId },
    });
}

/** Fetch all available device types (stored procedure). */
export function fetchAllDeviceTypes(campusId) {
    return apiFetch({
        procedure: 'getAllDeviceTypes',
        reqType: 'stored',
        pKey: 'campus_id',
        pVal: campusId,
        payload: { campus_id: campusId },
    });
}

/** Fetch campus-specific cloud reason codes (stored procedure). */
export function fetchReasonCodes(campusId) {
    return apiFetch({
        procedure: 'getReasonCodes',
        reqType: 'stored',
        pKey: 'campus_id',
        pVal: campusId,
        payload: { campus_id: campusId },
    });
}

/** Fetch global reason code reference table. */
export function fetchGlobalReasonCodes(campusId) {
    return apiFetch({
        table: 'att_palcare_reasoncode',
        procedure: 'fetchall',
        reqType: 'direct',
        pKey: 'campus_id',
        pVal: campusId,
    });
}

/** Fetch preform event note templates (stored procedure). */
export function fetchPreformNotes(campusId) {
    return apiFetch({
        procedure: 'getpreformnotes',
        reqType: 'stored',
        pKey: 'campus_id',
        pVal: campusId,
    });
}

// ---------------------------------------------------------------------------
// WRITE operations — Alert Settings
// ---------------------------------------------------------------------------

/**
 * Save alert settings (core_campus_alert_settings).
 * Caller is responsible for converting minutes → seconds before calling.
 */
export function saveAlertSettings(payload, oldPayload, { campusId, userId, clientId }) {
    return apiSave({
        table: 'core_campus_alert_settings',
        pKey: 'alert_setting_id',
        condition: 'primary',
        payload,
        oldPayload,
        campusId,
        userId,
        clientId,
        method: 'POST',
    });
}

// ---------------------------------------------------------------------------
// WRITE operations — Customization
// ---------------------------------------------------------------------------

/** Save customization fields on core_campus. */
export function saveCustomSettings(payload, oldPayload, { campusId, userId, clientId }) {
    return apiSave({
        table: 'core_campus',
        pKey: 'campus_id',
        condition: 'primary',
        payload,
        oldPayload,
        campusId,
        userId,
        clientId,
        method: 'POST',
    });
}

// ---------------------------------------------------------------------------
// WRITE operations — High Priority
// ---------------------------------------------------------------------------

/** Save high-priority entity selections via transaction. */
export function savePriorityEntities(payload, oldPayload, { campusId, userId, clientId }) {
    return apiTransaction({
        transType: 'prioritySave',
        payload,
        oldPayload,
        campusId,
        userId,
        clientId,
    });
}

// ---------------------------------------------------------------------------
// WRITE operations — AFR Devices
// ---------------------------------------------------------------------------

/** Save AFR device selections via transaction. */
export function saveAFRDevices(payload, oldPayload, { campusId, userId, clientId }) {
    return apiTransaction({
        transType: 'afrSave',
        payload,
        oldPayload,
        campusId,
        userId,
        clientId,
    });
}

// ---------------------------------------------------------------------------
// WRITE operations — Reason Codes
// ---------------------------------------------------------------------------

/** Add a new cloud reason code. */
export function addReasonCode(payload, { campusId, userId, clientId }) {
    return apiSave({
        table: 'core_reason_codes',
        pKey: 'cloud_reason_code_id',
        condition: 'primary',
        payload,
        oldPayload: [],
        campusId,
        userId,
        clientId,
        method: 'PUT',
    });
}

/** Edit an existing cloud reason code. */
export function editReasonCode(payload, oldPayload, { campusId, userId, clientId }) {
    return apiSave({
        table: 'core_reason_codes',
        pKey: 'cloud_reason_code_id',
        condition: 'primary',
        payload,
        oldPayload,
        campusId,
        userId,
        clientId,
        method: 'POST',
    });
}

/** Delete one or more cloud reason codes via transaction. */
export function deleteReasonCodes(payload, { campusId, userId, clientId }) {
    return apiTransaction({
        transType: 'reason_delete',
        payload,
        campusId,
        userId,
        clientId,
    });
}

// ---------------------------------------------------------------------------
// WRITE operations — Preform Notes
// ---------------------------------------------------------------------------

/** Add a new preform event note template. */
export function addPreformNote(payload, { campusId, userId, clientId }) {
    return apiSave({
        table: 'core_preform_event_notes',
        pKey: 'preform_note_id',
        condition: 'primary',
        payload,
        oldPayload: [],
        campusId,
        userId,
        clientId,
        method: 'PUT',
    });
}

/** Edit an existing preform event note template. */
export function editPreformNote(payload, oldPayload, { campusId, userId, clientId }) {
    return apiSave({
        table: 'core_preform_event_notes',
        pKey: 'preform_note_id',
        condition: 'primary',
        payload,
        oldPayload,
        campusId,
        userId,
        clientId,
        method: 'POST',
    });
}

/** Delete preform event note templates. */
export function deletePreformNotes(payload, { campusId, userId, clientId }) {
    return apiSave({
        table: 'core_preform_event_notes',
        pKey: 'preform_note_id',
        payload,
        campusId,
        userId,
        clientId,
        method: 'DELETE',
    });
}

/** Import preform notes from another campus via transaction. */
export function importPreformNotes(payload, { campusId, userId, clientId }) {
    return apiTransaction({
        transType: 'preform_notes_import',
        payload,
        campusId,
        userId,
        clientId,
    });
}

// ---------------------------------------------------------------------------
// WRITE operations — Server Settings
// ---------------------------------------------------------------------------

/**
 * Save server settings (emergency_email, emergency_text, system_offline_time).
 * These are fields on the core_campus row.
 */
export function saveServerSettings(payload, oldPayload, { campusId, userId, clientId }) {
    return apiSave({
        table: 'core_campus',
        pKey: 'campus_id',
        condition: 'primary',
        payload,
        oldPayload,
        campusId,
        userId,
        clientId,
        method: 'POST',
    });
}
